import { SuppliesInventoryConfigStatus } from "@redotech/cockroachdb-codegen/tables/supplies-inventory-config-status";
import { AccountId, accountIdFormat } from "@redotech/redo-account-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const SuppliesInventoryConfigStatusSchema = createEnum(
  SuppliesInventoryConfigStatus,
);

/** How a supplies order is paid. */
//redontIgnore
export enum SuppliesPaymentMethod {
  /** Debited against the merchant's OMS ("outbound-labels") balance ledger. */
  OmsBalance = "oms_balance",
  /** Charged immediately to a saved Stripe payment method (card or ACH). */
  Stripe = "stripe",
}

/**
 * Net payment terms for supplies. Set on the team; absence means charge
 * immediately to the checkout-chosen method, presence defers to primary/secondary.
 */
//redontIgnore
export enum SuppliesPaymentTerms {
  /** Defer collection to 15 days after placement. */
  Net15 = "net_15",
  /** Defer collection to 30 days after placement. */
  Net30 = "net_30",
}
export const SuppliesPaymentTermsSchema = createEnum(SuppliesPaymentTerms);

/**
 * Reference to a saved merchant funding source. Mirrors the
 * `FundingSourceReference` type in @redotech/redo-billing-common/funding-source.
 */
export const FundingSourceReferenceSchema = z.union([
  z.object({ source: z.literal("outbound_labels"), id: z.string() }),
  z.object({
    source: z.literal("general"),
    //redontIgnore
    priority: z.enum(["primary", "secondary", "card"]),
  }),
]);

/**
 * How a supplies order is paid: the OMS balance ledger, or a charge to one of
 * the merchant's saved Stripe payment methods (referenced directly).
 */
export const SuppliesPaymentMethodChoiceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal(SuppliesPaymentMethod.OmsBalance) }),
  z.object({
    kind: z.literal(SuppliesPaymentMethod.Stripe),
    reference: FundingSourceReferenceSchema,
  }),
]);
export type SuppliesPaymentMethodChoice = z.infer<
  typeof SuppliesPaymentMethodChoiceSchema
>;

declare global {
  // Substituted at build time in browser dev; in Node the env var is used.
  var SUPPLIES_SOURCE_ACCOUNT_ID: string | undefined;
  var SUPPLIES_SOURCE_TEAM_ID: string | undefined;
}

export function getRedoSuppliesAccountInfo(): {
  accountId: AccountId;
  teamId: string;
} {
  if (process.env.NODE_ENV === "production") {
    return {
      accountId: accountIdFormat.write(BigInt("2209160522559597213")),
      teamId: "69334cb38b62ab405f172655",
    };
  } else {
    return {
      accountId: accountIdFormat.write(
        BigInt(
          globalThis.SUPPLIES_SOURCE_ACCOUNT_ID ||
            (typeof process !== "undefined"
              ? process.env.SUPPLIES_SOURCE_ACCOUNT_ID
              : undefined) ||
            "",
        ),
      ),
      teamId:
        globalThis.SUPPLIES_SOURCE_TEAM_ID ||
        (typeof process !== "undefined"
          ? process.env.SUPPLIES_SOURCE_TEAM_ID
          : undefined) ||
        "",
    };
  }
}

export { SuppliesInventoryConfigStatus };
